import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Fab,
  darken,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import type { Nullable } from '@/types/types';
import type { User } from '@/types/api';
import TeamMemberManager from '@/features/teams/TeamMemberManager';
import PencilOutlinedIcon from '@/components/icons/PencilOutlinedIcon';
import CustomDataGrid from '@/components/CustomDataGrid';
import { sendInvite, useUsers } from '@/hooks/admin/useUsers';
import { Company } from '@/types/api';
import { useCompanies } from '@/hooks/admin/useCompanies';
import { SnackBarContext } from 'pragmatic-ui';

const TeamTable = () => {
  const { data: users, isLoading } = useUsers();
  const { data: companies = [] } = useCompanies();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [teamMemberBeingRemoved, setTeamMemberBeingRemoved] =
    useState<Nullable<User>>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const companyId = searchParams.get('companyId');
  const { showSnack } = useContext(SnackBarContext);

  /**
   * initialState
   */
  const initialState = useMemo(() => {
    const state: any = {
      pagination: { paginationModel: { pageSize: 10 } },
    };

    if (companyId) {
      state.filter = {
        filterModel: {
          items: [
            {
              field: 'currentCompany',
              operatorValue: 'equals',
              value: companies.find((currentCompany) => currentCompany._id === companyId)
                ?.name,
            },
          ],
        },
      };
    }
    return state;
  }, [companyId, companies]);

  if (!users) return null;

  /**
   * handleEditButtonClicked
   */
  const handleEditButtonClicked = (user: User) => {
    //console.log('user', user);
    setSelectedUser(user);
    setDrawerOpen(true);
  };

  /**
   * handleSendInviteEmail
   */
  const handleSendInviteEmail = ({ email, name }: { email: string, name: string}) => async () => {
    const result = await sendInvite({ email, name})
    if(result) showSnack('Send invite email successfully', 'success');
  }

  /**
   * columns
   */
  const columns: GridColDef[] = [
    {
      field: 'gravatar',
      headerName: '',
      minWidth: 60,
      align: 'center',
      flex: 1,
      renderCell: (params) => {
        return (
          <Box
            component="div"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={'100%'}
          >
            <Avatar src={params.row.gravatar} />
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      minWidth: 300,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 250,
      flex: 1,
    },
    {
      field: 'currentCompany',
      headerName: 'Client',
      minWidth: 250,
      flex: 1,
      valueGetter: (currentCompany: Company) => currentCompany?.name,
    },
    {
      field: 'type',
      headerName: 'Role',
      minWidth: 100,
      flex: 1,
      valueGetter: (val) =>
        String(val).charAt(0).toUpperCase() + String(val).slice(1),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 100,
      flex: 1,
      renderCell: ({ row }) => {
        const isActive = row.status === 'active';
        const isInviting = row.status === 'invited';
        return isActive ? 'Active' : isInviting ? <Button sx={{
          p: 0,
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          }
        }} onClick={handleSendInviteEmail({ email: row.email, name: row.name})}>Send Invite</Button> : <></>
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 150,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <Fab
            color="primary"
            size="small"
            aria-label="edit"
            sx={{
              mr: 2,
              '&:hover': {
                backgroundColor: darken('#F6AE2D', 0.2),
              },
            }}
            onClick={() => handleEditButtonClicked(params.row)}
          >
            <PencilOutlinedIcon />
          </Fab>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <Box>
        <CustomDataGrid
          rows={users}
          getRowId={(user) => user._id}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={users.length}
          loading={isLoading}
          initialState={initialState}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>

      {/* Remove team member dialog */}
      <Dialog
        open={!!teamMemberBeingRemoved}
        onClose={() => setTeamMemberBeingRemoved(null)}
        maxWidth="xs"
      >
        <DialogTitle>Remove Team Member</DialogTitle>
        <DialogContent>
          Are you sure you would like to remove this person from the team?
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setTeamMemberBeingRemoved(null)}
            color="primary"
          >
            Cancel
          </Button>

          <Button
            // onClick={removeTeamMember}
            disabled={isLoading}
            variant="contained"
            size="small"
            color="primary"
            sx={{ ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* User edit drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: '30% !important',
            background: '#333C45',
            borderRadius: '12px 0px 0px 12px !important',
          },
        }}
      >
        <div role="presentation">
          <Box m={4}>
            <TeamMemberManager
              setDrawerOpen={setDrawerOpen}
              selectedUser={selectedUser}
            />
          </Box>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default TeamTable;
